import api from '@/lib/services/general.service'
import { toast } from 'vue3-toastify';
import router from '@/router';

export const state = {
    emails: {
        items: [],
        index: 0,
        totalCount: 0,
        totalPages: 0,
        hasPreviousPage: 0,
        hasNextPage: 0,
    },
    draftTimelines: {
        items: [],
        index: 0,
        totalCount: 0,
        totalPages: 0,
        hasPreviousPage: 0,
        hasNextPage: 0,
    },
    requestData: {
        page: 1,
        order: 1,
        pageSize: 50
    },
    isLoading: false,
}
export const getters = {
    draftEmails: s => s.emails,
    draftTimelines: s => s.draftTimelines,
    isLoading: s => s.isLoading,
    draftCounts: s => s.draftTimelines?.totalCount || 0,
    requestData: s => s.requestData,
};
export const actions = {
    patchDraftBody({ commit }, { emailId, contentHtml }) {
        commit('PATCH_DRAFT_BODY', { emailId, contentHtml })
    },
    // `silentToast` (busy-my-web#245): the SmartBox composer reports the send itself, in ONE place,
    // with View message and Undo on it — two announcements of one send in two places is exactly
    // what was reported. Opt-in, like `email/postEmail`'s, so the mail form is unchanged.
    async sendDraft({ commit, dispatch }, email) {
        const silent = !!email.silentToast
        delete email.silentToast
        const toastId = silent ? null : toast.loading(
            'Please wait...', {
            theme: 'email',
        });
        commit('SET_LOADING', true);
        let reported = false // did the send get far enough to say something itself?
        try {
            await api.insert(`email/draft/send`, email).then(response => {
                if (response) {
                    commit('SET_LOADING', false);
                    commit("REMOVE_DRAFT", email.messageId)
                    dispatch('timeline/refreshSends', {}, { root: true });
                    dispatch('timeline/addEmailIntoTimeline', email, { root: true });
                    dispatch('sidepanel/addEmailIntoTimeline', email, { root: true });
                    reported = true
                    if (!silent) setTimeout(() => {
                        toast.update(toastId, {
                            render: "Message Sent",
                            autoClose: true,
                            closeOnClick: true,
                            closeButton: true,
                            theme: 'email',
                            isLoading: false,
                        });
                        toast.done(toastId);
                    });
                    return true
                }
            })
        } catch (err) {
            return false
        } finally {
            // The spinner closes whatever happened (busy-my-web#247). A loading notification has no
            // timeout of its own, so a send that threw — or that answered nothing — used to leave
            // "Please wait..." in the box permanently, with every later notification stacking above
            // it. `toast.done` is a no-op on one that has already reported.
            commit('SET_LOADING', false);
            if (toastId && !reported) toast.done(toastId);
        }
    },
    async fetchDraft({ commit }, msgId) {
        try {
            return await api.getById(`email/draft`, msgId).then(response => {
                if (response) {
                    commit("SET_DRAFT", response.data)
                    return true
                }
            })
        } catch (err) {
            return false
        }
    },
    async CreateOrUpdateDraft({ dispatch }, email) {
        try {
            const response = await api.insert(`email/draft`, email);
            if (response && router.currentRoute.value.path !== '/emails/draft') {
                dispatch('fetchEmails', null);
            }
            return response;
        } catch (err) {
            return false;
        }
    },
    async fetchEmails({ commit, state, dispatch }, newRequestData) {
        let requestData = null;
        if (newRequestData === null) {
            requestData = {
                page: state.requestData.page,
                order: state.requestData.order,
                pageSize: state.requestData.pageSize,
            }
        } else {
            requestData = newRequestData;
        }
        commit('SET_REQUEST_DATA', requestData);
        commit('SET_LOADING', true);
        try {
            await fetchDraftsWithRetry(commit, dispatch, requestData, 0);
        } finally {
            commit('SET_LOADING', false);
        }
    },
    async eraseEmail({ dispatch, commit, state }, requestData) {
        commit('SET_LOADING', true);
        const allUids = requestData
            .filter(e => e.provider !== null && e.uId)
            .map(e => e.uId.toString());

        const emailMessageIds = requestData
            .map(e => e.messageId)
            .filter(Boolean);
        const request = { emailMessageIds };
        try {
            await api.insert(`email/eraseDraft`, request);

            // Remove draft emails immediately from state
            if (allUids.length > 0) {
                commit('REMOVE_EMAIL', allUids);
            }

            // Also remove by messageId for drafts without uId
            emailMessageIds.forEach(messageId => {
                commit('REMOVE_DRAFT', messageId);
            })

            // Remove draft emails from their associated timelines
            requestData.forEach(email => {
                if (email.timelineId && email.messageId) {
                    dispatch('timeline/deleteTimelineItems', {
                        timelineId: email.timelineId,
                        itemId: email.messageId,
                        itemType: 'emails'
                    }, { root: true })
                }
            })

            const emailsTotalCount = state.draftTimelines?.totalCount || 0;
            const pageSize = state.requestData.pageSize || 0;
            if (emailsTotalCount > pageSize) {
                setTimeout(() => {
                    dispatch('fetchEmails', null);
                }, 3000);
            }

            commit('SET_LOADING', false);
            return true;
        } catch (error) {
            console.error("Error erasing emails:", error);
            throw error;
        }
    },
}
async function fetchDraftsWithRetry(commit, dispatch, requestData, retryCount) {
    try {
        const requestParams = { ...requestData };
        const responseV2 = await api.getV2(`email/draft`, requestParams)
        if (responseV2) {
            commit("SET_DRAFT_TIMELINES", responseV2.data);
            dispatch('globalCreationForms/mergeDrafts', {}, { root: true })
            commit('SET_LOADING', false);
        }
    } catch (error) {
        console.error('Error fetching Emails:', error);
        if (retryCount < 3) {
            await new Promise(resolve => setTimeout(resolve, 10000));
            await fetchDraftsWithRetry(commit, dispatch, requestData, retryCount + 1);
        } else {
            console.error('Max retries reached. Failed to fetch Emails.');
        }
    }
}
export const mutations = {
    SET_REQUEST_DATA(s, data) {
        s.requestData.page = data.page
        s.requestData.pageSize = data.pageSize
        s.requestData.order = data.order
        s.requestData.search = data.search
    },
    SET_EMAILS(s, emails) {
        s.emails = {}
        if (emails?.items) {
            emails.items = emails.items.map(email => ({
                ...email,
                attachments: (email.attachments || []).map(att => ({
                    ...att,
                    isDraft: true,
                    hasThumbnail: false, // thumbnails live in doc service which doesn't sync drafts
                    messageId: att.messageId || email.messageId
                }))
            }))
        }
        s.emails = emails;
    },
    SET_DRAFT_TIMELINES(s, payload, dispatch) {
        if (payload?.items) {
            const expandedItems = []

            payload.items.forEach(item => {
                // Check if this is an orphaned draft timeline (has zero UUID or no meaningful id)
                const isOrphanedTimeline = !item.id || item.id === '00000000-0000-0000-0000-000000000000'

                // For orphaned timelines, expand each draft into its own timeline item
                if (isOrphanedTimeline && item.emailMessages?.length > 0) {
                    item.emailMessages.forEach(msg => {
                        expandedItems.push({
                            ...item,
                            id: 'orphaned-draft-' + msg.messageId,
                            headLine: msg.subject || 'Unsent Draft',
                            emailMessages: [{
                                ...msg,
                                attachments: (msg.attachments || []).map(att => ({
                                    ...att,
                                    isDraft: msg.mainFolder === 8 ? true : !!att.isDraft,
                                    hasThumbnail: false,
                                    messageId: att.messageId || msg.messageId
                                }))
                            }]
                        })
                    })
                } else {
                    // Normal timeline - keep as is
                    expandedItems.push({
                        ...item,
                        emailMessages: (item.emailMessages || []).map(msg => ({
                            ...msg,
                            attachments: (msg.attachments || []).map(att => ({
                                ...att,
                                isDraft: msg.mainFolder === 8 ? true : !!att.isDraft,
                                hasThumbnail: false,
                                messageId: att.messageId || msg.messageId
                            }))
                        }))
                    })
                }
            })

            payload.items = expandedItems
        }
        s.draftTimelines = payload;
    },
    SET_LOADING(s, loading) {
        s.isLoading = loading;

    },
    PATCH_DRAFT_BODY(s, { emailId, contentHtml }) {
        const email = s.emails.items.find(item => item.id === emailId)
        if (email) email.contentHtml = contentHtml
        if (s.draftTimelines?.items) {
            for (const item of s.draftTimelines.items) {
                const msg = (item.emailMessages || []).find(m => m.id === emailId)
                if (msg) msg.contentHtml = contentHtml
            }
        }
    },
    SET_DRAFT(s, draft) {
        const index = s.emails.items.findIndex(item => item.messageId === draft.messageId);
        if (index !== -1) {
            s.emails.items[index] = { ...s.emails.items[index], contentHtml: draft.contentHtml };
        }
        if (s.draftTimelines?.items) {
            s.draftTimelines.items = s.draftTimelines.items.map(item => ({
                ...item,
                emailMessages: (item.emailMessages || []).map(msg =>
                    msg.messageId === draft.messageId
                        ? { ...msg, contentHtml: draft.contentHtml }
                        : msg
                )
            }));
        }
    },
    REMOVE_EMAIL(state, emailIds) {
        const initialCount = state.emails.items.length;
        state.emails.items = state.emails.items.filter(email => !emailIds.includes(email.uId));
        const removedCount = initialCount - state.emails.items.length;
        state.emails.totalCount -= removedCount;

        if (state.draftTimelines?.items) {
            const prevTlCount = state.draftTimelines.items.length;
            state.draftTimelines.items = state.draftTimelines.items.filter(item => {
                const draft = (item.emailMessages || []).find(m => m.mainFolder === 8) ||
                    (item.emailMessages || [])[(item.emailMessages || []).length - 1];
                return !draft || !emailIds.includes(draft.uId);
            });
            state.draftTimelines.totalCount -= (prevTlCount - state.draftTimelines.items.length);
        }
    },
    REMOVE_DRAFT(s, messageId) {
        const prevLength = s.emails.items.length;
        s.emails.items = s.emails.items.filter(item => item.messageId !== messageId);
        if (s.emails.items.length < prevLength) {
            s.emails.totalCount--;
        }
        if (s.draftTimelines?.items) {
            const prevTlLength = s.draftTimelines.items.length;
            s.draftTimelines.items = s.draftTimelines.items.filter(item =>
                !(item.emailMessages || []).some(m => m.messageId === messageId && m.mainFolder === 8)
            );
            if (s.draftTimelines.items.length < prevTlLength) {
                s.draftTimelines.totalCount--;
            }
        }
    }

}
export default {
    state,
    getters,
    actions,
    mutations
};